import { AiIcon } from "../components/AiIcon";
import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { api } from "../api";
import type { Abstract, Event, ScientificTable, Session } from "../types";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { Modal } from "../components/Modal";
import {
  ActionIcon,
  AdminActionButton,
  DeleteIcon,
  EditIcon,
  IconActionButton,
  IconActionLink,
} from "../components/AdminActions";
import { AbstractTableAssignmentModal } from "../components/AbstractTableAssignmentModal";
import { MaterialPreviewModal } from "../components/MaterialPreviewModal";

type Props = {
  event: Event;
  abstracts: Abstract[];
  scientificTables: ScientificTable[];
  sessions: Session[];
  loading: boolean;
  onNew: () => void;
  onEdit: (item: Abstract) => void;
  onDelete: (item: Abstract) => void;
  onStatus: (item: Abstract, status: Abstract["status"]) => void;
  onPdfAiReview: (item: Abstract, file: File) => Promise<void>;
  onReload: () => Promise<void> | void;
};

export function AbstractsPage({
  event,
  abstracts: suppliedAbstracts,
  scientificTables,
  sessions,
  loading,
  onNew,
  onEdit,
  onDelete,
  onStatus,
  onPdfAiReview,
  onReload,
}: Props) {
  const abstracts = useMemo(() => suppliedAbstracts.filter((item) => item.eventId === event.id), [suppliedAbstracts, event.id]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("submitted_desc");
  const [reviewCounts, setReviewCounts] = useState<number[]>([]);
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [selectedCorrection, setSelectedCorrection] = useState<Abstract | null>(
    null,
  );
  const [selectedPdf, setSelectedPdf] = useState<Abstract | null>(null);
  const [selectedDetails, setSelectedDetails] = useState<Abstract | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<{ title: string; fileName: string; mimeType: string; url: string } | null>(null);
  const [classifying, setClassifying] = useState(false);
  const [classificationMessage, setClassificationMessage] = useState("");
  const [tableAssignmentOpen, setTableAssignmentOpen] = useState(false);
  const [reviewerWork, setReviewerWork] = useState<Abstract | null>(null);
  const [reviewerOptions, setReviewerOptions] = useState<Array<{ id: number; name: string; email: string; country: string; institution: string; topicMatch: number; subeventMatch: number; reviewAllTopics: number; reviewAllSubevents: number; assigned: number }>>([]);
  const [reviewerBusy, setReviewerBusy] = useState(false);
  const [oralCutoff, setOralCutoff] = useState(event.oralCutoffScore ?? 8);
  const [posterCutoff, setPosterCutoff] = useState(
    event.posterCutoffScore ?? 6,
  );
  const [oralLimit, setOralLimit] = useState(event.oralSelectionLimit || 0);
  const [posterLimit, setPosterLimit] = useState(
    event.posterSelectionLimit || 0,
  );
  const availableReviewCounts = useMemo(
    () =>
      [...new Set(abstracts.map((item) => item.completedReviewerCount))].sort(
        (a, b) => a - b,
      ),
    [abstracts],
  );
  const filtered = useMemo(
    () =>
      abstracts
        .filter((item) => {
          const matches =
            !search ||
            `${item.code} ${item.title} ${item.submitterName}`
              .toLowerCase()
              .includes(search.toLowerCase());
          return (
            matches &&
            (status === "all" || item.status === status) &&
            (!reviewCounts.length ||
              reviewCounts.includes(item.completedReviewerCount))
          );
        })
        .sort((first, second) =>
          sort.startsWith("criterion:")
            ? ((second.criterionScores?.[sort.slice(10)] ?? -1) - (first.criterionScores?.[sort.slice(10)] ?? -1))
          : sort === "score_desc"
            ? (second.score ?? -1) - (first.score ?? -1)
            : sort === "score_asc"
              ? (first.score ?? 999) - (second.score ?? 999)
              : sort === "reviews_desc"
                ? second.completedReviewerCount -
                    first.completedReviewerCount ||
                  (second.score ?? -1) - (first.score ?? -1)
                : Date.parse(second.submittedAt) -
                  Date.parse(first.submittedAt),
        ),
    [abstracts, reviewCounts, search, sort, status],
  );
  const scored = abstracts.filter((item) => item.score != null);
  const average = scored.length
    ? scored.reduce((sum, item) => sum + (item.score || 0), 0) / scored.length
    : 0;
  const privateAccessUrl = event.callForScienceAccessUuid
    ? `https://call4science.virtualmedica.com/${event.id}/${event.callForScienceAccessUuid}`
    : "";
  const customAccessUrl = event.callForScienceSubdomain
    ? `https://${event.callForScienceSubdomain}.virtualmedica.com`
    : "";

  async function copyAccess(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      await Swal.fire({ toast: true, position: "top-end", icon: "success", title: `${label} copiado`, showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch {
      await Swal.fire({ icon: "error", title: "No se pudo copiar", text: "Seleccioná la dirección y copiala manualmente." });
    }
  }

  async function classify() {
    const confirmation = await Swal.fire({
      icon: "question",
      title: "¿Aplicar estos puntos de corte?",
      text: "Se actualizarán los trabajos evaluados como Oral, Póster o Rechazado según el ranking.",
      showCancelButton: true,
      confirmButtonText: "Aplicar selección",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#169c98",
      cancelButtonColor: "#708090",
      reverseButtons: true,
      focusCancel: true,
    });
    if (!confirmation.isConfirmed) return;
    setClassifying(true);
    setClassificationMessage("");
    try {
      const result = await api<{
        oral: number;
        poster: number;
        rejected: number;
        unscored: number;
      }>("/api/abstracts/classify", {
        method: "POST",
        body: JSON.stringify({
          eventId: event.id,
          oralCutoffScore: oralCutoff,
          posterCutoffScore: posterCutoff,
          oralSelectionLimit: oralLimit,
          posterSelectionLimit: posterLimit,
        }),
      });
      setClassificationMessage(
        `Selección aplicada: ${result.oral} orales · ${result.poster} pósters · ${result.rejected} rechazados · ${result.unscored} sin puntaje.`,
      );
      await onReload();
    } catch (error) {
      setClassificationMessage(
        error instanceof Error
          ? error.message
          : "No se pudo aplicar la selección",
      );
    } finally {
      setClassifying(false);
    }
  }

  async function openReviewers(item: Abstract) {
    setReviewerWork(item); setReviewerBusy(true);
    try { const result = await api<{ reviewers: typeof reviewerOptions }>(`/api/abstracts/${item.id}/reviewers?eventId=${event.id}`); setReviewerOptions(result.reviewers); }
    finally { setReviewerBusy(false); }
  }
  async function changeReviewer(reviewerId: number, action: "assign" | "unassign") {
    if (!reviewerWork) return; setReviewerBusy(true);
    try { await api(`/api/abstracts/${reviewerWork.id}/reviewers`, { method: "POST", body: JSON.stringify({ eventId: event.id, reviewerId, action }) }); await onReload(); await openReviewers(reviewerWork); }
    catch (error) { await Swal.fire({ icon: "error", title: "No se pudo actualizar el arbitraje", text: error instanceof Error ? error.message : "Error inesperado" }); setReviewerBusy(false); }
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Call for Science</span>
          <h1>Trabajos científicos</h1>
          <p>Recepción, evaluación por jurados y resolución final.</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="button button-secondary abstracts-table-assignment-button"
            onClick={() => setTableAssignmentOpen(true)}
          >
            <ActionIcon name="grid" />
            <span>Asignación de mesas</span>
          </button>
          <IconActionButton
            label="Nuevo trabajo"
            icon="add"
            tone="primary"
            onClick={onNew}
          />
        </div>
      </header>
      <section className="panel call4science-access-panel" aria-labelledby="call4science-access-title">
        <header>
          <div>
            <span className="eyebrow">Accesos del evento</span>
            <h2 id="call4science-access-title">Enlaces de Call for Science</h2>
            <p>Compartí el acceso personalizado. El sistema lo redirige al enlace privado protegido por UUID.</p>
          </div>
        </header>
        <div className="call4science-access-grid">
          <article>
            <span>Acceso personalizado</span>
            {customAccessUrl ? <>
              <a href={customAccessUrl} target="_blank" rel="noreferrer">{customAccessUrl}</a>
              <div className="call4science-access-actions">
                <IconActionButton label="Copiar acceso personalizado" icon="copy" onClick={() => void copyAccess(customAccessUrl, "Acceso personalizado")} />
                <IconActionLink label="Abrir acceso personalizado" icon="external" href={customAccessUrl} target="_blank" rel="noreferrer" />
              </div>
            </> : <p className="call4science-access-empty">Todavía no se configuró un subdominio personalizado en el evento.</p>}
          </article>
          <article>
            <span>Enlace privado</span>
            {privateAccessUrl ? <>
              <a href={privateAccessUrl} target="_blank" rel="noreferrer">{privateAccessUrl}</a>
              <dl><dt>UUID</dt><dd>{event.callForScienceAccessUuid}</dd></dl>
              <div className="call4science-access-actions">
                <IconActionButton label="Copiar enlace privado" icon="copy" onClick={() => void copyAccess(privateAccessUrl, "Enlace privado")} />
                <IconActionButton label="Copiar UUID" icon="copy" onClick={() => void copyAccess(event.callForScienceAccessUuid || "", "UUID")} />
                <IconActionLink label="Abrir enlace privado" icon="external" href={privateAccessUrl} target="_blank" rel="noreferrer" />
              </div>
            </> : <p className="call4science-access-error">El backend todavía no informó el UUID. Actualizá y reiniciá Organize.</p>}
          </article>
        </div>
      </section>
      <section className="mini-metric-row abstracts-metrics">
        <div>
          <span>Recibidos</span>
          <strong>{abstracts.length}</strong>
        </div>
        <div>
          <span>En evaluación</span>
          <strong>
            {
              abstracts.filter((item) =>
                ["pending", "in_review"].includes(item.status),
              ).length
            }
          </strong>
        </div>
        <div>
          <span>Aceptados</span>
          <strong>
            {abstracts.filter((item) => item.status === "accepted").length}
          </strong>
        </div>
        <div>
          <span>Puntaje promedio</span>
          <strong>{average.toFixed(1)}</strong>
        </div>
      </section>
      <section className="panel table-panel">
        <div className="abstract-selection-panel">
          <div>
            <span className="eyebrow">Resolución científica</span>
            <h2>Puntos de corte y cupos</h2>
            <p>Se usa {event.classificationMetric === "average" ? "el promedio general ponderado" : `el puntaje de ${event.reviewCriteria?.find((criterion) => criterion.id === event.classificationMetric)?.name || "la métrica seleccionada"}`} de todos los arbitrajes válidos y se respeta el ranking.</p>
          </div>
          <div className="abstract-selection-fields">
            <label>
              <span>Corte oral</span>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={oralCutoff}
                onChange={(change) =>
                  setOralCutoff(Number(change.target.value))
                }
              />
            </label>
            <label>
              <span>Cupo oral</span>
              <input
                type="number"
                min="0"
                value={oralLimit}
                onChange={(change) => setOralLimit(Number(change.target.value))}
              />
            </label>
            <label>
              <span>Corte póster</span>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={posterCutoff}
                onChange={(change) =>
                  setPosterCutoff(Number(change.target.value))
                }
              />
            </label>
            <label>
              <span>Cupo póster</span>
              <input
                type="number"
                min="0"
                value={posterLimit}
                onChange={(change) =>
                  setPosterLimit(Number(change.target.value))
                }
              />
            </label>
            <IconActionButton
              label="Aplicar selección"
              icon="check"
              tone="primary"
              busy={classifying}
              disabled={classifying}
              onClick={classify}
            />
          </div>
          {classificationMessage && (
            <div className="inline-notice">{classificationMessage}</div>
          )}
          <small>Un cupo en 0 significa sin límite.</small>
        </div>
        <div className="table-tools">
          <div className="search-field">
            <span>⌕</span>
            <input
              value={search}
              onChange={(change) => setSearch(change.target.value)}
              placeholder="Buscar por ID, título o responsable"
              aria-label="Buscar trabajos"
            />
          </div>
          <select
            value={status}
            onChange={(change) => setStatus(change.target.value)}
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="in_review">En evaluación</option>
            <option value="accepted">Aceptados</option>
            <option value="rejected">Rechazados</option>
            <option value="withdrawn">Retirados</option>
          </select>
          <select
            value={sort}
            onChange={(change) => setSort(change.target.value)}
          >
            <option value="submitted_desc">Más recientes</option>
            <option value="score_desc">Puntaje promedio: mayor a menor</option>
            <option value="score_asc">Puntaje promedio: menor a mayor</option>
            <option value="reviews_desc">Más arbitrajes realizados</option>
            {(event.reviewCriteria || []).map((criterion) => <option value={`criterion:${criterion.id}`} key={criterion.id}>{criterion.name}: mayor a menor</option>)}
          </select>
          <label className="abstract-review-count-filter">
            <span>Arbitrajes realizados</span>
            <select
              multiple
              value={reviewCounts.map(String)}
              onChange={(change) =>
                setReviewCounts(
                  [...change.currentTarget.selectedOptions].map((option) =>
                    Number(option.value),
                  ),
                )
              }
            >
              {availableReviewCounts.map((count) => (
                <option value={count} key={count}>
                  {count} arbitraje{count === 1 ? "" : "s"}
                </option>
              ))}
            </select>
            <small>
              {reviewCounts.length
                ? `${reviewCounts.length} cantidades seleccionadas`
                : "Todas las cantidades"}{" "}
              · Ctrl/Cmd para selección múltiple
            </small>
          </label>
          <span className="result-count">{filtered.length} trabajos</span>
        </div>
        {loading ? (
          <div className="skeleton page-skeleton short" />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Sin trabajos"
            text="No hay trabajos que coincidan con la búsqueda."
          />
        ) : (
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Trabajo</th>
                  <th>Responsable</th>
                  {event.aiOrganizeAvailable && <th>Corrección por IA</th>}
                  <th>Arbitrajes</th>
                  {(event.reviewCriteria || []).map((criterion) => <th key={criterion.id}>{criterion.name}</th>)}
                  <th>Puntaje promedio</th>
                  <th>Estado</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <button
                        type="button"
                        className="abstract-id-view-button"
                        title={`Ver trabajo ${item.code}`}
                        aria-label={`Ver trabajo ${item.code}: ${item.title}`}
                        onClick={() => setSelectedDetails(item)}
                      >
                        <ActionIcon name="view" />
                        <span>{item.code}</span>
                      </button>
                    </td>
                    <td className="title-cell">
                      <strong>{item.title}</strong>
                      <span>
                        {item.topicName || item.category || "Sin temática"} ·{" "}
                        {item.country} ·{" "}
                        {item.preferredFormat === "oral" ? "Oral" : "Póster"}
                        {item.scientificTableName
                          ? ` · ${item.scientificTableName}`
                          : ""}
                      </span>
                    </td>
                    <td>
                      <div>{item.submitterName}</div>
                      <small>{item.submitterEmail}</small>
                    </td>
                    {event.aiOrganizeAvailable && <td className="abstract-pdf-cell">
                      <div className="abstract-ai-actions">
                        {event.aiOrganizeAvailable ? (
                          <label
                            className={`abstract-ai-upload secondary ${reviewingId === item.id ? "busy" : ""}`}
                            aria-busy={reviewingId === item.id}
                            title={item.pdfUrl ? "Reemplazar y corregir con IA" : "Cargar y corregir con IA"}
                          >
                            {reviewingId === item.id ? <ActionIcon name="refresh" /> : <AiIcon />}
                            <input
                              type="file"
                              aria-label={item.aiCorrectionComments ? "Volver a corregir con IA" : "Corregir con IA"}
                              accept="application/pdf,.pdf"
                              disabled={reviewingId != null}
                              onChange={async (change) => {
                                const file = change.target.files?.[0];
                                change.target.value = "";
                                if (!file) return;
                                setReviewingId(item.id);
                                try {
                                  await onPdfAiReview(item, file);
                                } finally {
                                  setReviewingId(null);
                                }
                              }}
                            />
                          </label>
                        ) : null}
                        {item.aiCorrectionComments && (
                          <IconActionButton
                            label={`Ver corrección por IA${item.aiCorrectionScore == null ? "" : ` · ${item.aiCorrectionScore.toFixed(1)}`}`}
                            icon="report"
                            onClick={() => setSelectedCorrection(item)}
                          />
                        )}
                      </div>
                    </td>}
                    <td>
                      <strong>
                        {item.completedReviewerCount}/
                        {item.assignedReviewerCount}
                      </strong>
                      {event.aiJuryAvailable && Boolean(item.aiReviewerAssigned) && (
                        <small className="ai-review-state">
                          IA {item.aiReviewerCompleted ? "✓" : "pendiente"}
                        </small>
                      )}
                      <IconActionButton label="Asignar / desasignar" icon="users" tone="secondary" type="button" onClick={() => void openReviewers(item)} />
                    </td>
                    {(event.reviewCriteria || []).map((criterion) => <td key={criterion.id}>{item.criterionScores?.[criterion.id] == null ? <span className="muted">—</span> : <strong className="score">{item.criterionScores[criterion.id]?.toFixed(1)}</strong>}</td>)}
                    <td>
                      {item.score == null ? (
                        <span className="muted">Sin evaluar</span>
                      ) : (
                        <strong className="score">
                          {item.score.toFixed(1)}
                        </strong>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>
                      <div className="row-actions wide-actions">
                        {item.status !== "withdrawn" && item.status !== "accepted" && (
                          <IconActionButton
                            label={`Aceptar ${item.title}`}
                            icon="check"
                            tone="success"
                            onClick={() => onStatus(item, "accepted")}
                          />
                        )}
                        {item.status !== "withdrawn" && item.status !== "rejected" && (
                          <IconActionButton
                            label={`Rechazar ${item.title}`}
                            icon="cancel"
                            tone="danger"
                            onClick={() => onStatus(item, "rejected")}
                          />
                        )}
                        {item.status !== "withdrawn" && !["pending", "in_review"].includes(item.status) && (
                          <IconActionButton
                            label={`Restablecer ${item.title} a pendiente`}
                            icon="refresh"
                            tone="secondary"
                            onClick={() => onStatus(item, "pending")}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {selectedDetails && (
        <Modal
          title={`${selectedDetails.code} · ${selectedDetails.title}`}
          eyebrow="Expediente completo del trabajo"
          wide
          className="abstract-details-modal"
          onClose={() => setSelectedDetails(null)}
        >
          <div className="abstract-details-summary">
            <div><span>Estado</span><StatusBadge status={selectedDetails.status} /></div>
            <div><span>Tipo</span><strong>{workTypeLabel(selectedDetails.workType)}</strong></div>
            <div><span>Subevento</span><strong>{selectedDetails.subeventTitle || "Sin subevento"}</strong></div>
            <div><span>Temática / modalidad</span><strong>{[selectedDetails.topicName, selectedDetails.modeName || "Indistinta"].filter(Boolean).join(" · ") || "Sin informar"}</strong></div>
          </div>
          {selectedDetails.status === "withdrawn" && <div className="inline-notice">Retirado por el autor{selectedDetails.withdrawnAt ? ` el ${new Date(selectedDetails.withdrawnAt).toLocaleString("es-AR")}` : ""}. Se conserva el expediente y queda fuera de evaluación.</div>}
          <div className="abstract-details-grid">
            <section><h3>Responsable de comunicaciones</h3><p><strong>{selectedDetails.contactName || selectedDetails.submitterName}</strong>{selectedDetails.contactRole ? ` · ${selectedDetails.contactRole}` : ""}</p><p>{selectedDetails.contactEmail || selectedDetails.submitterEmail} · {selectedDetails.contactPhone || "Sin WhatsApp"}</p></section>
            <section><h3>Datos del envío</h3><p>País: {selectedDetails.country || "Sin informar"}</p><p>Formato: {selectedDetails.preferredFormat || "Sin informar"}</p><p>Presentado: {selectedDetails.submittedAt ? new Date(selectedDetails.submittedAt).toLocaleString("es-AR") : "Sin informar"}</p></section>
            <section className="abstract-details-full"><h3>Autores</h3>{selectedDetails.authors?.length ? <ol>{selectedDetails.authors.map((author) => <li key={author.id}><strong>{author.name}</strong><span>{[author.institution, author.email, author.whatsapp].filter(Boolean).join(" · ")}</span></li>)}</ol> : <p>Sin autores informados.</p>}</section>
            {(selectedDetails.abstractText || selectedDetails.contentHtml) && <section className="abstract-details-full"><h3>Contenido</h3><p className="abstract-details-content">{selectedDetails.abstractText || htmlToPlainText(selectedDetails.contentHtml)}</p></section>}
            <section className="abstract-details-full"><h3>Archivos</h3><div className="abstract-details-files">{selectedDetails.sourceUrl ? <IconActionButton label="Archivo original" icon="view" tone="secondary" type="button" onClick={() => setSelectedMaterial({ title: selectedDetails.title, fileName: selectedDetails.sourceName, mimeType: selectedDetails.sourceMime, url: selectedDetails.sourceUrl })} /> : <span className="muted">Sin archivo original</span>}{selectedDetails.pdfUrl && <IconActionButton label="PDF del trabajo" icon="view" tone="secondary" type="button" onClick={() => { setSelectedPdf(selectedDetails); setSelectedDetails(null); }} />}{selectedDetails.presentationUrl && <IconActionButton label="Presentación final" icon="view" tone="secondary" type="button" onClick={() => setSelectedMaterial({ title: selectedDetails.title, fileName: selectedDetails.presentationName, mimeType: selectedDetails.presentationMime, url: selectedDetails.presentationUrl })} />}</div></section>
          </div>
          <div className="modal-footer">
            {selectedDetails.status !== "withdrawn" && (
              <AdminActionButton tone="edit" label={`Editar ${selectedDetails.code}`} onClick={() => { setSelectedDetails(null); onEdit(selectedDetails); }}>
                <EditIcon />
              </AdminActionButton>
            )}
            <AdminActionButton tone="delete" label={`Eliminar ${selectedDetails.code}`} onClick={() => { setSelectedDetails(null); onDelete(selectedDetails); }}>
              <DeleteIcon />
            </AdminActionButton>
            <IconActionButton label="Cerrar" icon="close" tone="primary" onClick={() => setSelectedDetails(null)} />
          </div>
        </Modal>
      )}
      {selectedCorrection && (
        <Modal
          title={`Corrección · ${selectedCorrection.code}`}
          eyebrow="Trabajo científico"
          wide
          className="abstract-correction-modal"
          onClose={() => setSelectedCorrection(null)}
        >
          <div className="abstract-correction-summary">
            <div>
              <span>Puntaje</span>
              <strong>
                {selectedCorrection.aiCorrectionScore?.toFixed(1)} / 10
              </strong>
            </div>
            <div>
              <span>Trabajo</span>
              <strong>{selectedCorrection.title}</strong>
            </div>
            <div>
              <span>Fecha de corrección</span>
              <strong>
                {selectedCorrection.aiCorrectedAt
                  ? new Date(selectedCorrection.aiCorrectedAt).toLocaleString(
                      "es-AR",
                    )
                  : "Sin informar"}
              </strong>
            </div>
          </div>
          <CorrectionContent text={selectedCorrection.aiCorrectionComments} />
          <div className="modal-footer abstract-correction-actions">
            {selectedCorrection.pdfUrl && (
              <IconActionButton
                label="Ver trabajo original"
                icon="report"
                onClick={() => {
                  setSelectedPdf(selectedCorrection);
                  setSelectedCorrection(null);
                }}
              />
            )}
            <IconActionButton
              label="Cerrar"
              icon="close"
              onClick={() => setSelectedCorrection(null)}
            />
            <IconActionLink
              label="Descargar original y corrección"
              icon="download"
              tone="primary"
              href={`/api/abstracts/${selectedCorrection.id}/ai-correction.pdf?eventId=${selectedCorrection.eventId}`}
              download
            />
          </div>
        </Modal>
      )}
      {selectedMaterial && <MaterialPreviewModal title={selectedMaterial.title} eyebrow="Vista de la presentación" fileName={selectedMaterial.fileName} mimeType={selectedMaterial.mimeType} url={selectedMaterial.url} showDownload={false} onClose={() => setSelectedMaterial(null)} />}
      {selectedPdf && (
        <Modal
          title={`${selectedPdf.code} · ${selectedPdf.title}`}
          eyebrow="Visualización del trabajo"
          wide
          className="abstract-pdf-modal"
          onClose={() => setSelectedPdf(null)}
        >
          <div className="abstract-pdf-viewer">
            <iframe
              src={selectedPdf.pdfUrl}
              title={`Trabajo ${selectedPdf.code}`}
            />
          </div>
          <div className="modal-footer">
            <IconActionLink
              label="Descargar PDF"
              icon="download"
              href={selectedPdf.pdfUrl}
              download
            />
            <IconActionButton
              label="Cerrar"
              icon="close"
              tone="primary"
              onClick={() => setSelectedPdf(null)}
            />
          </div>
        </Modal>
      )}
      {tableAssignmentOpen && (
        <AbstractTableAssignmentModal
          eventId={event.id}
          abstracts={abstracts}
          scientificTables={scientificTables}
          sessions={sessions}
          onReload={onReload}
          onClose={() => setTableAssignmentOpen(false)}
        />
      )}
      {reviewerWork && <Modal title={`Árbitros · ${reviewerWork.code}`} eyebrow="Asignación manual" wide className="reviewer-assignment-modal" onClose={() => setReviewerWork(null)}>
        <p>La asignación automática prioriza unidad temática, excluye autores y evita —en este orden— el mismo servicio y el mismo país.</p>
        {reviewerBusy && !reviewerOptions.length ? <div className="skeleton page-skeleton short" /> : <div className="reviewer-assignment-list">{reviewerOptions.map((reviewer) => { const compatible = (reviewer.reviewAllTopics || reviewer.topicMatch) && (reviewer.reviewAllSubevents || reviewer.subeventMatch); return <article key={reviewer.id}><div><strong>{reviewer.name}</strong><span>{[reviewer.institution, reviewer.country, reviewer.email].filter(Boolean).join(" · ")}</span><small className={compatible ? "compatible" : "warning"}>{compatible ? "Compatible con temática y subevento" : "Fuera de la temática o subevento configurado"}</small></div><IconActionButton label={reviewer.assigned ? "Desasignar" : "Asignar"} icon="users" tone="secondary" type="button" disabled={reviewerBusy} onClick={() => void changeReviewer(reviewer.id, reviewer.assigned ? "unassign" : "assign")} /></article>; })}</div>}
        <div className="modal-footer"><IconActionButton label="Cerrar" icon="close" tone="primary" onClick={() => setReviewerWork(null)} /></div>
      </Modal>}
    </div>
  );
}

function CorrectionContent({ text }: { text: string }) {
  const blocks = text
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
  return (
    <div className="abstract-correction-content">
      {blocks.map((block, index) => {
        const lines = block
          .split(/\n/)
          .map((line) => line.trim())
          .filter(Boolean);
        if (lines[0]?.toLowerCase().startsWith("recomendación:"))
          return (
            <p className="abstract-correction-recommendation" key={index}>
              {lines.join(" ")}
            </p>
          );
        if (lines[0]?.endsWith(":"))
          return (
            <section key={index}>
              <h3>{lines[0].slice(0, -1)}</h3>
              <ul>
                {lines.slice(1).map((line, lineIndex) => (
                  <li key={lineIndex}>{line.replace(/^[-•]\s*/, "")}</li>
                ))}
              </ul>
            </section>
          );
        return (
          <p key={index}>
            {lines.map((line, lineIndex) => (
              <span key={lineIndex}>
                {line}
                {lineIndex < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function workTypeLabel(value: Abstract["workType"]) {
  return value === "clinical_case" ? "Caso clínico" : value === "poster" ? "ePoster" : "Abstract";
}

function htmlToPlainText(value: string) {
  if (!value) return "";
  const container = document.createElement("div");
  container.innerHTML = value;
  return container.textContent || "";
}
